import type { DetailedCatalystInsight } from '@api/api/catalyst-insights';
import { formatTicker } from '@api/mappers/formatTicker';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import {
  getEntityTagFromSnake,
  getTickersFromSnakes,
} from '@helpers/utils/entity/entity';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { EXPANDED_VALUE } from '@terminal/components/expandable-drawer/ExpandableDrawer';
import { usePriceMarketReactionData } from '@terminal/hooks/use-price-market-reaction-data/usePriceMarketReactionData';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { CatalystMarketReactionChart } from '@terminal/modules/market-reaction-charts/catalyst-market-reaction-chart/CatalystMarketReactionChart';
import { useEntity } from '@terminal/react-query-hooks/entities/useEntities';
import { useTheme } from '@terminal/react-query-hooks/themes/useThemeOverview';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useTranslation } from 'react-i18next';

import { MarketCatalystReaction } from '../catalyst-widget/components/market-catalyst-reaction/MarketCatalystReaction';
import styles from './CatalystDrawerHeader.module.scss';
import {
  renderCatalystMetadataHeader,
  renderCatalystSubjectHeader,
  renderLegacyChartEntityHeader,
} from './catalystDrawerHeaderRenderUtils';
import { getCatalystHeaderIdentifiers } from './catalystDrawerHeaderUtils';

export interface CatalystDrawerHeaderProps {
  identifier: string;
  catalystInsight: DetailedCatalystInsight;
  isCompanyCatalyst?: boolean;
}

const legacyCompanyChartConfig = {
  chartHeaderVariant: 'default',
  trackingSource: undefined,
} as const;

const redesignedCompanyChartConfig = {
  chartHeaderVariant: 'companyCatalyst',
  trackingSource: POSTHOG_TRACKING_SOURCES.CATALYST_INSIGHT_DRAWER,
} as const;

export const CatalystDrawerHeader = ({
  identifier,
  catalystInsight,
  isCompanyCatalyst,
}: CatalystDrawerHeaderProps) => {
  const { t } = useTranslation('insights');
  const { params } = useQueryParams();
  const isInsightDrawerHeaderEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-4942-insight-drawer-header')
  );
  const isExpanded = params[queryKeys.drawerExpanded] === EXPANDED_VALUE;

  const { entityTag, themeId } = getCatalystHeaderIdentifiers({
    catalystInsight,
    identifier,
    isCompanyCatalyst,
  });
  const { data: entityData } = useEntity(entityTag);

  const { data: themeData } = useTheme({
    themeId,
  });
  const entity = entityData?.data;
  const isMultipleEntitiesChart = catalystInsight.snakes.length > 1;
  const shouldShowRedesignedCompanyHeader =
    isInsightDrawerHeaderEnabled && !!isCompanyCatalyst;
  const isThematicMarketCatalyst =
    isInsightDrawerHeaderEnabled &&
    !isCompanyCatalyst &&
    catalystInsight.sub_category === 'thematic' &&
    !!themeId;
  const marketChartTrackingSource = isThematicMarketCatalyst
    ? POSTHOG_TRACKING_SOURCES.MARKET_CATALYST_DRAWER
    : undefined;
  const companyChartConfig = shouldShowRedesignedCompanyHeader
    ? redesignedCompanyChartConfig
    : legacyCompanyChartConfig;

  const {
    priceData,
    fromDateEST,
    toDateEST,
    lastProportionChange,
    lastKnownPrice,
    prevCloseData,
    lastKnownDate,
    isDailyFallback,
    isLoading: isPriceMarketReactionDataLoading,
  } = usePriceMarketReactionData({
    entityTag,
    ticker: entity?.subscribable_ticker ?? '',
    reportingDate: catalystInsight.created_at,
    enabled: isCompanyCatalyst,
    isMultipleEntitiesChart,
    variant: 'catalyst',
  });

  /* i18next-extract-mark-context-next-line ["financial_operational_updates", "corporate_actions_strategic_announcements", "management_leadership_changes", "legal_regulatory_events"] */
  const translatedSubCategory = t(
    `catalyst.subcategories.${catalystInsight.sub_category}`
  );

  return (
    <div className={styles.root}>
      {!isExpanded &&
        renderCatalystSubjectHeader({
          catalystInsight,
          entity,
          isCompanyCatalyst,
          shouldShowRedesignedCompanyHeader,
          theme: themeData,
          translatedSubCategory,
        })}
      {renderCatalystMetadataHeader({
        catalystInsight,
        isCompanyCatalyst,
        shouldShowRedesignedCompanyHeader,
        themeId,
        translatedSubCategory,
      })}
      <div className={styles.titleGrid}>
        <div className={styles.chartRow}>
          <ErrorBoundaryWrapper>
            {isPriceMarketReactionDataLoading ? (
              <div className={styles.styledSkeletonLoaderWrapper}>
                <SkeletonLoader
                  areas={[
                    {
                      styles: {
                        width: '100%',
                        height: '400px',
                        borderRadius: '8px',
                      },
                    },
                  ]}
                />
              </div>
            ) : isCompanyCatalyst ? (
              <CatalystMarketReactionChart
                fromDateEST={fromDateEST}
                toDateEST={toDateEST}
                isDailyFallback={isDailyFallback}
                chartHeaderVariant={companyChartConfig.chartHeaderVariant}
                lines={
                  priceData && entity
                    ? [
                        {
                          data: priceData,
                          label: formatTicker(entity),
                          prevCloseData,
                          lastProportionChange,
                          lastKnownPrice,
                          lastKnownDate,
                          subject: { type: 'entity', data: entity },
                        },
                      ]
                    : []
                }
                leftHeaderNode={renderLegacyChartEntityHeader({
                  entity,
                  isMultipleEntitiesChart,
                  trackingSource: companyChartConfig.trackingSource,
                  variant:
                    companyChartConfig.chartHeaderVariant === 'companyCatalyst'
                      ? 'compact'
                      : 'default',
                })}
              />
            ) : (
              <MarketCatalystReaction
                createdAt={catalystInsight.created_at}
                tags={catalystInsight.snakes.map(getEntityTagFromSnake)}
                tickers={getTickersFromSnakes(catalystInsight.snakes)}
                themeId={themeId}
                leftHeaderNode={renderLegacyChartEntityHeader({
                  entity,
                  isMultipleEntitiesChart,
                  trackingSource: marketChartTrackingSource,
                })}
                chartHeaderVariant={
                  isThematicMarketCatalyst ? 'marketCatalyst' : 'default'
                }
                trackingSource={marketChartTrackingSource}
              />
            )}
          </ErrorBoundaryWrapper>
        </div>
      </div>
    </div>
  );
};
